import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, ShieldCheck, Flag, Trash2, MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EASE } from '../lib/motion';

interface RowActionsMenuProps {
  onMarkContacted: () => void;
  onMarkQualified: () => void;
  onSetHighPriority: () => void;
  onDelete: () => void;
  label: string;
}

/** Kebab menu used on each lead row/card. Closes on outside click and Escape. */
export function RowActionsMenu({ onMarkContacted, onMarkQualified, onSetHighPriority, onDelete, label }: RowActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const runAndClose = (fn: () => void) => () => {
    fn();
    setOpen(false);
  };

  return (
    <div className="relative inline-block text-left" ref={containerRef}>
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setOpen((v) => !v)} aria-label={`Actions for ${label}`}>
        <MoreVertical className="h-4 w-4" aria-hidden="true" />
      </Button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -4 }}
            transition={{ duration: 0.14, ease: EASE }}
            className="absolute right-0 top-9 z-20 w-44 rounded-md border border-border bg-card py-1 shadow-elevation-3"
          >
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-foreground hover:bg-muted"
              onClick={runAndClose(onMarkContacted)}
            >
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> Mark contacted
            </button>
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-foreground hover:bg-muted"
              onClick={runAndClose(onMarkQualified)}
            >
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" /> Mark qualified
            </button>
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-foreground hover:bg-muted"
              onClick={runAndClose(onSetHighPriority)}
            >
              <Flag className="h-3.5 w-3.5" aria-hidden="true" /> Set high priority
            </button>
            <div className="my-1 h-px bg-border" />
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-destructive hover:bg-destructive/10"
              onClick={runAndClose(onDelete)}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> Delete
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
